import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLine, FadeUp } from "./MaskedReveal";

const chapters = [
  {
    n: "01",
    title: "Fly with reverence.",
    body:
      "Aviation is a covenant with physics and with people. Every checklist, every callout, every knot of crosswind is a small act of respect. We train that respect until it becomes muscle.",
  },
  {
    n: "02",
    title: "Discipline over drama.",
    body:
      "Beautiful flying is quiet flying. We build pilots and programs that lower the noise: precise flows, deliberate energy management, no theatrics — just the machine, the mission, and the answer.",
  },
  {
    n: "03",
    title: "Own the room, own the ramp.",
    body:
      "Command begins on the ground. From dispatch briefing to hangar handoff, we cultivate a presence — with owners, crews, and controllers — that matches the caliber of the airplanes we fly.",
  },
  {
    n: "04",
    title: "Worldwide, without shortcuts.",
    body:
      "We deliver aircraft across oceans and continents with the same care we bring to a training flight around the pattern. The horizon changes; the standard does not.",
  },
];

export const Manifesto = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section
      id="manifesto"
      ref={ref}
      className="relative bg-[#050505] py-24 md:py-40 overflow-hidden"
      data-testid="manifesto-section"
    >
      <motion.div
        style={{ y: bgY }}
        className="pointer-events-none absolute right-[-10%] top-1/2 -translate-y-1/2 font-serif-italic text-[26rem] md:text-[40rem] text-white/[0.025] leading-none select-none"
        aria-hidden
      >
        Elyon
      </motion.div>

      <div className="relative mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-20">
          <div className="md:col-span-6">
            <FadeUp>
              <div className="font-mono-label text-white/50 mb-6">
                <span className="text-[#FF5A00]">◉</span> Manifesto · Chapters
              </div>
            </FadeUp>
            <h2 className="font-display text-5xl md:text-7xl lg:text-8xl uppercase leading-[0.86] tracking-tight">
              <MaskedLine>Four beliefs</MaskedLine>
              <MaskedLine delay={0.15}>we fly by.</MaskedLine>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-y-24 md:gap-y-40">
          {chapters.map((c, i) => (
            <div
              key={c.n}
              className={`col-span-1 md:col-span-10 ${
                i % 2 === 0 ? "md:col-start-1" : "md:col-start-3"
              }`}
              data-testid={`manifesto-chapter-${c.n}`}
            >
              <div className="grid grid-cols-12 gap-6 items-start">
                <div className="col-span-3 md:col-span-3">
                  <FadeUp>
                    <div className="chapter-number text-[9rem] md:text-[16rem] lg:text-[20rem]">
                      {c.n}
                    </div>
                  </FadeUp>
                </div>
                <div className="col-span-9 md:col-span-8 md:col-start-5 pt-4 md:pt-16">
                  <FadeUp>
                    <h3 className="font-display text-3xl md:text-5xl lg:text-6xl uppercase tracking-tight leading-[0.95] mb-6">
                      {c.title}
                    </h3>
                  </FadeUp>
                  <FadeUp delay={0.15}>
                    <p className="text-white/60 text-base md:text-lg leading-relaxed max-w-2xl">
                      {c.body}
                    </p>
                  </FadeUp>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
